/* Importing the necessary libraries and files */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

/* Importing the components from the material-ui library. */
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

/* Importing components from the components folder. */
import AutoCompletePatient from '../../components/AutoCompletePatient';
import AutoCompleteLocation from '../../components/AutoCompleteLocation';
import DeviceCard from '../../components/DeviceCard';
import FormHelpDialog from '../../components/FormHelpDialog';
import Page from '../../components/Page';

/* Importing the functions from the Redux slices. */
import { getDevice, postDevice, putDevice } from '../../features/device/deviceSlice';

/* Importing constants from DeviceValueSets.js file. */
import {
  DEVICES,
  DEVICE_NAME_TYPE,
  DEVICE_TEMPLATE,
  STATUS,
  STATUS_REASON,
} from '../../utils/Device/Device.utils.ValueSets';

/* Importing the functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import getInitialValues from '../../utils/getInitialValues';
import handleFormResponse from '../../utils/handleFormResponse';
import { fDevice } from '../../utils/Device/Device.utils.format';
import { mDevice } from '../../utils/Device/Device.utils.map';

/* Defining the propTypes for the DeviceAdd component. */
DeviceAdd.propTypes = {
  isEdit: PropTypes.bool,
};

/**
 * The component that renders the device add page.
 * It contains a form that allows the user to add a new device.
 * @typedef {Object} DevicePageProps
 * @property {boolean} isEdit - Indicates whether the page is in edit mode or not.
 * @returns {JSX.Element} - A React component that renders the Device Add page.
 */
export default function DeviceAdd(props) {
  const { isEdit } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const { Devices } = useSelector((state) => state.device);
  const currentDevice = Devices.find((device) => device.id === params.id);

  /* Setting the states of objects. */
  const [title, setTitle] = useState('Add Device');
  const [isFormHidden, setIsFormHidden] = useState(true);
  const [isSelectDeviceHidden, setIsSelectDeviceHidden] = useState(false);
  const [pageDevice, setPageDevice] = useState({});
  const [formHelpOpen, setFormHelpOpen] = useState(false);
  const [valueSet, setValueSet] = useState(null);

  const handleFormHelpOpen = (valueSet) => {
    setValueSet(valueSet);
    setFormHelpOpen(true);
  };

  /**
   * Sets the device form to the given device type.
   */
  const setDeviceFormTemplate = (deviceType) => {
    if (Object.prototype.hasOwnProperty.call(DEVICE_TEMPLATE, deviceType)) {
      const deviceObj = formik.values;
      Object.keys(DEVICE_TEMPLATE[deviceType]).forEach((key) => {
        deviceObj[key] = DEVICE_TEMPLATE[deviceType][key];
      });
      setValues(getInitialValues({ data: deviceObj, defaultValues }));
      setIsFormHidden(false);
    }
  };

  const defaultValues = useMemo(
    () => ({
      status: '',
      statusReason: '',
      manufacturer: '',
      serialNumber: '',
      deviceName_name: '',
      deviceName_type: '',
      patient: '',
      location: '',
      url: '',
      type: '',
    }),
    []
  );

  /* Creating a schema for the Device form. */
  const DeviceSchema = Yup.object().shape({
    deviceName_name: Yup.string().min(1, 'Too Short!').max(50, 'Too Long!').required('Device name required'),
    deviceName_type: Yup.string().required("Device name's type required"),
  });

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: getInitialValues({
      defaultValues,
      data: pageDevice,
    }),
    validationSchema: DeviceSchema,
    onSubmit: (values) => {
      const deviceData = mDevice(values);
      if (isEdit) {
        submitPutDevice(deviceData);
      } else {
        submitPostDevice(deviceData);
      }
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue, values, setValues } = formik;

  const submitPostDevice = async (values) => {
    const resultAction = await dispatch(postDevice(values));
    await handleFormResponse({
      resultAction,
      successTitle: 'Device added successfully!',
      navigatePath: '/device/details/:id',
      navigate,
    });
  };

  const submitPutDevice = async (values) => {
    const data = {
      deviceID: params.id,
      data: { ...values, id: params.id },
    };
    const resultAction = await dispatch(putDevice(data));
    await handleFormResponse({
      resultAction,
      successTitle: 'Device updated successfully!',
      navigatePath: '/device/details/:id',
      navigate,
    });
  };

  useEffect(() => {
    setTitle(isEdit && pageDevice?.deviceName_name ? `Edit Device - ${pageDevice.deviceName_name}` : 'Edit Device');
  }, [isEdit, pageDevice.deviceName_name]);

  useEffect(() => {
    if (!currentDevice) return;
    const deviceObj = fDevice(currentDevice);
    setPageDevice(deviceObj);
    setValues(getInitialValues({ data: deviceObj, defaultValues }));
    setIsFormHidden(false);
  }, [currentDevice, defaultValues, setValues]);

  /* A function that is called when the page loads. */
  const initializeData = useCallback(() => {
    setIsSelectDeviceHidden(!!isEdit);
    setIsFormHidden(true);

    if (isEdit) {
      fetchAndHandleData({
        dispatch,
        navigate,
        fetchAction: getDevice,
        id: params.id,
        errorMessage: 'No Device record found',
        navigateOnFailure: true,
        navigateTo: '/device/list/',
      });
    }
  }, [dispatch, isEdit, navigate, params.id]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Rendering the header of the page. */
  const renderHeader = () => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
      <Stack direction="column" justifyContent="space-between">
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>

        <Breadcrumbs aria-label="breadcrumb" mr={4}>
          <Link underline="hover" color="inherit" component={RouterLink} to="/device/list">
            Device
          </Link>

          {isEdit && (
            <Link underline="hover" color="inherit" component={RouterLink} to={`/device/details/${params.id}`}>
              {pageDevice.deviceName_name || pageDevice.id}
            </Link>
          )}

          <Typography color="text.primary">{isEdit ? 'Edit' : 'Add'} </Typography>
        </Breadcrumbs>
      </Stack>
    </Stack>
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        {renderHeader()}
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            {!isSelectDeviceHidden && (
              <Grid container spacing={3} sx={{ pb: 2 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Select a Device to Add</Typography>
                </Grid>
                {DEVICES.map((device) => (
                  <Grid item xs={12} sm={6} md={2} key={`${device.name}-grid`}>
                    <DeviceCard device={device} onClick={() => setDeviceFormTemplate(device.name)} />
                  </Grid>
                ))}
              </Grid>
            )}

            {!isFormHidden && (
              <Grid container>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                        <Grid item xs={12} md={12}>
                          <Stack spacing={3}>
                            <Stack spacing={2}>
                              <Typography variant="subtitle1" sx={{ pt: 1 }}>
                                Device Identification
                              </Typography>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label="Device Name*"
                                {...getFieldProps('deviceName_name')}
                                error={Boolean(touched.deviceName_name && errors.deviceName_name)}
                                helperText={touched.deviceName_name && errors.deviceName_name}
                              />
                              <TextField
                                fullWidth
                                select
                                label="Device Name's Type*"
                                {...getFieldProps('deviceName_type')}
                                error={Boolean(touched.deviceName_type && errors.deviceName_type)}
                                helperText={touched.deviceName_type && errors.deviceName_type}
                              >
                                {DEVICE_NAME_TYPE.values.map((el) => (
                                  <MenuItem key={el.code} value={el.code}>
                                    {el.display}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <IconButton
                                aria-label="help"
                                onClick={() => {
                                  handleFormHelpOpen(DEVICE_NAME_TYPE);
                                }}
                              >
                                <HelpOutlineIcon />
                              </IconButton>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label="Serial Number"
                                {...getFieldProps('serialNumber')}
                                error={Boolean(touched.serialNumber && errors.serialNumber)}
                                helperText={touched.serialNumber && errors.serialNumber}
                              />
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label="Device Type"
                                {...getFieldProps('type')}
                                error={Boolean(touched.type && errors.type)}
                                helperText={touched.type && errors.type}
                              />
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label="Manufacturer"
                                {...getFieldProps('manufacturer')}
                                error={Boolean(touched.manufacturer && errors.manufacturer)}
                                helperText={touched.manufacturer && errors.manufacturer}
                              />
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label="URL"
                                {...getFieldProps('url')}
                                error={Boolean(touched.url && errors.url)}
                                helperText={touched.url && errors.url}
                              />
                            </Stack>

                            <Stack spacing={2}>
                              <Typography variant="subtitle1" sx={{ pt: 1 }}>
                                Device Status
                              </Typography>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                select
                                label="Status"
                                {...getFieldProps('status')}
                                error={Boolean(touched.status && errors.status)}
                                helperText={touched.status && errors.status}
                              >
                                <MenuItem value="">None</MenuItem>
                                {STATUS.values.map((el) => (
                                  <MenuItem key={el.code} value={el.code}>
                                    {el.display}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <IconButton
                                aria-label="help"
                                onClick={() => {
                                  handleFormHelpOpen(STATUS);
                                }}
                              >
                                <HelpOutlineIcon />
                              </IconButton>

                              <TextField
                                fullWidth
                                select
                                label="Status Reason"
                                {...getFieldProps('statusReason')}
                                error={Boolean(touched.statusReason && errors.statusReason)}
                                helperText={touched.statusReason && errors.statusReason}
                                onChange={(e) => {
                                  e.preventDefault();
                                  formik.setFieldValue('statusReason', e.target.value);
                                }}
                              >
                                <MenuItem value="">None</MenuItem>
                                {STATUS_REASON.values.map((el) => (
                                  <MenuItem key={el.code} value={el.code}>
                                    {el.display}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <IconButton
                                aria-label="help"
                                onClick={() => {
                                  handleFormHelpOpen(STATUS_REASON);
                                }}
                              >
                                <HelpOutlineIcon />
                              </IconButton>
                            </Stack>

                            <Stack spacing={2}>
                              <Typography variant="subtitle1" sx={{ pt: 1 }}>
                                Associated Patient
                              </Typography>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <AutoCompletePatient
                                setFieldValue={setFieldValue}
                                fieldName={'patient'}
                                values={values}
                                touched={touched}
                                errors={errors}
                                patientID={isEdit ? values?.patient : null}
                              />
                            </Stack>

                            <Stack spacing={2}>
                              <Typography variant="subtitle1" sx={{ pt: 1 }}>
                                Location
                              </Typography>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <AutoCompleteLocation
                                setFieldValue={setFieldValue}
                                fieldName={'location'}
                                values={values}
                                touched={touched}
                                errors={errors}
                                locationID={isEdit ? values?.location : null}
                              />
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>

                      <Grid container sx={{ pt: 3 }}>
                        <Grid item xs={12} md={8}>
                          <Typography variant="caption" sx={{ pt: 1 }}>
                            * Required Fields
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                              fullWidth
                              size="large"
                              variant="contained"
                              component={RouterLink}
                              to={isEdit ? `/device/details/${params.id}` : '/device/list'}
                              color="error"
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              fullWidth
                              size="large"
                              type="submit"
                              variant="contained"
                              loading={isSubmitting}
                            >
                              {isEdit ? 'Update' : 'Register'}
                            </LoadingButton>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Form>
        </FormikProvider>
        <FormHelpDialog isOpen={formHelpOpen} setIsOpen={setFormHelpOpen} valueSet={valueSet} />
      </Container>
    </Page>
  );
}
